import { useState, useRef, useCallback, useEffect } from 'preact/hooks';
import { apiSend } from '../api.js';
import { generateUUID } from '../utils/uuid.js';
import { Tracking } from '../utils/tracking.js';
import { createTipsState } from './useTipsApi.js';

export function useChatApi({
  messages,
  setMessages,
  input,
  setInput,
  inputRef,
  sharedToken,
  config,
  stickToBottomRef,
  shouldForceFinalScrollRef,
  ignoreScrollEventsRef,
  isPanelOpenRef,
  setActivePanel
}) {
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef(null);

  /**
   * Merges local expert tips metadata (`has_local_expert_tip`, `message_id`) found
   * anywhere in the Query Responder stream into the assistant message state.
   */
  const applyTipsMeta = useCallback((assistantId, meta) => {
    if (!meta) return;
    setMessages((m) => m.map((msg) => {
      if (msg.id !== assistantId) return msg;
      const prev = msg.tips || createTipsState();
      return {
        ...msg,
        tips: {
          ...prev,
          available: meta.hasLocalExpertTip !== undefined ? meta.hasLocalExpertTip : prev.available,
          messageId: (meta.messageId !== undefined && meta.messageId !== null) ? meta.messageId : prev.messageId
        }
      };
    }));
  }, [setMessages]);

  const customLoadingMessages = config?.loadingMessages;
  const [currentStatusOverride, setCurrentStatusOverride] = useState('');
  const prevApiStatusRef = useRef('');

  const activeStreamingMessage = messages.find((m) => m.role === 'assistant' && (m.pending || m.streaming));
  const currentApiStatus = activeStreamingMessage?.status || '';

  useEffect(() => {
    if (!busy || !currentApiStatus) {
      setCurrentStatusOverride('');
      prevApiStatusRef.current = '';
      return;
    }
    if (currentApiStatus !== prevApiStatusRef.current) {
      prevApiStatusRef.current = currentApiStatus;
      if (Array.isArray(customLoadingMessages) && customLoadingMessages.length > 0) {
        const available = customLoadingMessages.filter((msg) => msg !== currentStatusOverride);
        const pool = available.length > 0 ? available : customLoadingMessages;
        const randomIndex = Math.floor(Math.random() * pool.length);
        setCurrentStatusOverride(pool[randomIndex]);
      }
    }
  }, [currentApiStatus, busy, customLoadingMessages, currentStatusOverride]);

  const getStatusText = useCallback((apiStatus) => {
    if (Array.isArray(customLoadingMessages) && customLoadingMessages.length > 0) {
      return currentStatusOverride || apiStatus;
    }
    return apiStatus;
  }, [customLoadingMessages, currentStatusOverride]);

  const send = useCallback(async (text, source = 'typed') => {
    if (sharedToken) return;
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput('');
    if (inputRef?.current) {
      inputRef.current.style.height = 'auto';
    }
    setBusy(true);
    if (stickToBottomRef) stickToBottomRef.current = true;

    const msgIndex = messages.filter((m) => m.role === 'user').length + 1;
    const userMsg = { id: generateUUID(), role: 'user', content };
    const assistantId = generateUUID();

    setMessages((m) => [
      ...m,
      userMsg,
      { id: assistantId, role: 'assistant', content: '', pending: true, streaming: true, panels: [], status: '', error: null },
    ]);

    const startTime = performance.now();
    const receivedPanelTypes = new Set();
    let errorOccurred = false;

    cancelRef.current = await apiSend(content, {
      onChatId: () => {
        Tracking.trackMessage(config?.client, msgIndex, content, source);
      },
      onTipsMeta: (meta) => applyTipsMeta(assistantId, meta),
      onChunk: (partial) => {
        setMessages((m) => m.map((msg) => {
          return msg.id === assistantId
            ? { ...msg, content: msg.content + partial, pending: false, streaming: true }
            : msg;
        }));
      },
      onPanel: (panelType, panelData) => {
        if (panelData) {
          receivedPanelTypes.add(panelType);
        }
        const panelId = `${assistantId}_${panelType}`;
        setMessages((m) => m.map((msg) => {
          if (msg.id !== assistantId) return msg;
          const panel = { type: panelType, data: panelData, id: panelId };
          const existingIdx = msg.panels.findIndex((p) => p.type === panelType);
          const panels = existingIdx >= 0
            ? msg.panels.map((p, i) => i === existingIdx ? panel : p)
            : [...msg.panels, panel];
          return { ...msg, panels };
        }));
        if (isPanelOpenRef?.current && panelData && setActivePanel) {
          setActivePanel({ type: panelType, data: panelData, messageId: assistantId });
        }
      },
      onStatus: (statusMessage) => {
        setMessages((m) => m.map((msg) => {
          return msg.id === assistantId ? { ...msg, status: statusMessage } : msg;
        }));
      },
      onError: (errorMessage) => {
        errorOccurred = true;
        setMessages((m) => m.map((msg) => {
          return msg.id === assistantId ? { ...msg, error: errorMessage, pending: false, streaming: false } : msg;
        }));
      },
      onDone: ({ hasAnswer = true, intent = 'direct', hasLocalExpertTip = false, messageId = null } = {}) => {
        if (stickToBottomRef?.current) {
          if (shouldForceFinalScrollRef) shouldForceFinalScrollRef.current = true;
          if (ignoreScrollEventsRef) ignoreScrollEventsRef.current = true;
        }
        applyTipsMeta(assistantId, { hasLocalExpertTip, messageId });
        setMessages((m) => m.map((msg) => {
          return msg.id === assistantId ? { ...msg, streaming: false } : msg;
        }));
        setBusy(false);
        cancelRef.current = null;

        const responseTime = Math.round(performance.now() - startTime);
        const responseStatus = (errorOccurred || !hasAnswer) ? 'no_results' : 'answered';
        Tracking.trackResponse(
          config?.client,
          msgIndex,
          content,
          responseStatus,
          intent,
          1,
          receivedPanelTypes.has('map') ? 'true' : 'false',
          responseTime
        );
      },
    });
  }, [input, busy, messages, config, sharedToken, setMessages, stickToBottomRef, shouldForceFinalScrollRef, ignoreScrollEventsRef, isPanelOpenRef, setActivePanel, inputRef, applyTipsMeta]);

  const retry = useCallback(async (failedId) => {
    const failedIndex = messages.findIndex((m) => m.id === failedId);
    if (failedIndex === -1) return;
    const userMsg = messages[failedIndex - 1];
    if (!userMsg || userMsg.role !== 'user') return;

    if (busy) return;
    setBusy(true);
    if (stickToBottomRef) stickToBottomRef.current = true;

    setMessages((m) => m.map((msg) => {
      return msg.id === failedId
        ? { ...msg, content: '', pending: true, streaming: true, error: null, panels: [], status: '', tips: null }
        : msg;
    }));

    cancelRef.current = await apiSend(userMsg.content, {
      onTipsMeta: (meta) => applyTipsMeta(failedId, meta),
      onChunk: (partial) => {
        setMessages((m) => m.map((msg) => {
          return msg.id === failedId
            ? { ...msg, content: msg.content + partial, pending: false, streaming: true }
            : msg;
        }));
      },
      onPanel: (panelType, panelData) => {
        const panelId = `${failedId}_${panelType}`;
        setMessages((m) => m.map((msg) => {
          if (msg.id !== failedId) return msg;
          const panel = { type: panelType, data: panelData, id: panelId };
          const existingIdx = msg.panels.findIndex((p) => p.type === panelType);
          const panels = existingIdx >= 0
            ? msg.panels.map((p, i) => i === existingIdx ? panel : p)
            : [...msg.panels, panel];
          return { ...msg, panels };
        }));
        if (isPanelOpenRef?.current && panelData && setActivePanel) {
          setActivePanel({ type: panelType, data: panelData, messageId: failedId });
        }
      },
      onStatus: (statusMessage) => {
        setMessages((m) => m.map((msg) => {
          return msg.id === failedId ? { ...msg, status: statusMessage } : msg;
        }));
      },
      onError: (errorMessage) => {
        setMessages((m) => m.map((msg) => {
          return msg.id === failedId ? { ...msg, error: errorMessage, pending: false, streaming: false } : msg;
        }));
      },
      onDone: ({ hasLocalExpertTip = false, messageId = null } = {}) => {
        if (stickToBottomRef?.current) {
          if (shouldForceFinalScrollRef) shouldForceFinalScrollRef.current = true;
          if (ignoreScrollEventsRef) ignoreScrollEventsRef.current = true;
        }
        applyTipsMeta(failedId, { hasLocalExpertTip, messageId });
        setMessages((m) => m.map((msg) => {
          return msg.id === failedId ? { ...msg, streaming: false } : msg;
        }));
        setBusy(false);
        cancelRef.current = null;
      },
    });
  }, [messages, busy, setMessages, stickToBottomRef, shouldForceFinalScrollRef, ignoreScrollEventsRef, isPanelOpenRef, setActivePanel, applyTipsMeta]);

  return { busy, setBusy, cancelRef, getStatusText, send, retry };
}
